import React, { useEffect, useState } from "react";
import { Trophy, Star, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import GironeBadge from "@/components/GironeBadge";
import MvpBreakdownModal from "@/components/MvpBreakdownModal";

function publicFileUrl(imagePath) {
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!imagePath || !base) return null;
  return `${base}/api/public/files/${imagePath}`;
}

export default function WeeklyMvpCard() {
  const [mvp, setMvp] = useState(null);
  const [err, setErr] = useState(false);
  const [breakdown, setBreakdown] = useState(null); // {matchday, teamId, teamName}

  useEffect(() => {
    api.get("/public/mvp-weekly").then(({ data }) => setMvp(data)).catch(() => setErr(true));
  }, []);

  if (err || !mvp) return null;

  const openBreakdown = (teamId, teamName) => {
    if (!teamId) return;
    setBreakdown({ matchday: mvp.matchday, teamId, teamName });
  };

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="weekly-mvp-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(234,179,8,0.15)", color: "#eab308" }}>
          <Trophy className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Giornata {mvp.matchday} · Awards</div>
          <h2 className="display font-black text-2xl md:text-3xl tracking-tight">MVP della settimana</h2>
        </div>
      </div>

      <div className="relative rounded-3xl overflow-hidden text-white shadow-xl"
           style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #334155 100%)" }}
           data-testid="weekly-mvp-card">
        {/* Decorative rays */}
        <div className="absolute inset-0 opacity-25 pointer-events-none"
             style={{ background: "conic-gradient(from 210deg at 80% 30%, rgba(250,204,21,0.55) 0deg, transparent 60deg, rgba(59,86,224,0.35) 140deg, transparent 210deg, rgba(250,204,21,0.5) 330deg)" }} />
        <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full blur-3xl opacity-40 pointer-events-none" style={{ background: "#facc15" }} />

        <div className="relative grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-6 md:gap-8 items-center p-6 md:p-8">
          {/* Trophy medallion */}
          <div className="relative shrink-0 mx-auto md:mx-0">
            <div className="w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center shadow-2xl"
                 style={{ background: "radial-gradient(circle at 30% 30%, #fde047 0%, #eab308 55%, #a16207 100%)" }}>
              <Trophy className="w-12 h-12 md:w-14 md:h-14 text-slate-900" strokeWidth={2.5} />
            </div>
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest bg-white text-slate-900 shadow-md">
              MVP
            </div>
          </div>

          {/* Team info */}
          <div className="text-center md:text-left min-w-0">
            <div className="flex items-center gap-2 flex-wrap justify-center md:justify-start text-[10px] uppercase tracking-widest text-white/70">
              {mvp.girone && <GironeBadge girone={mvp.girone} size={16} />}
              <span>Girone {mvp.girone || "—"}</span>
              <span className="text-white/40">·</span>
              <span>Giornata {mvp.matchday}</span>
            </div>
            <button
              type="button"
              onClick={() => openBreakdown(mvp.user_id, mvp.team_name)}
              className="group display font-black text-3xl md:text-5xl mt-2 leading-tight uppercase text-white text-left md:text-inherit hover:text-yellow-300 transition-colors cursor-pointer underline-offset-4 hover:underline decoration-yellow-300/60"
              style={{ textShadow: "0 2px 20px rgba(250,204,21,0.35)" }}
              data-testid="mvp-team-name"
              aria-label={`Vedi dettaglio punteggio ${mvp.team_name}`}
            >
              {mvp.team_name}
              <span className="ml-2 text-xs align-middle text-white/40 group-hover:text-yellow-200 transition-colors">›</span>
            </button>
            {mvp.coach_name && (
              <div className="text-white/70 text-sm mt-1">Fantallenatore: <span className="text-white/90 font-semibold">{mvp.coach_name}</span></div>
            )}
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1.5 text-xs">
              <TrendingUp className="w-3.5 h-3.5" style={{ color: "#facc15" }} />
              <span className="text-white/85">
                +{mvp.delta_vs_avg?.toFixed(1)} pt sopra la media di giornata ({mvp.matchday_avg?.toFixed(1)})
              </span>
            </div>
          </div>

          {/* Score */}
          <div className="text-center md:text-right shrink-0" data-testid="mvp-points">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Punti</div>
            <div className="display font-black text-5xl md:text-6xl tabular-nums" style={{ color: "#facc15", textShadow: "0 4px 24px rgba(250,204,21,0.35)" }}>
              {mvp.points?.toFixed(1)}
            </div>
          </div>
        </div>

        {/* Runners-up strip (2° e 3° fantallenatore) */}
        {mvp.runners_up?.length > 0 && (
          <div className="relative border-t border-white/10 bg-black/25 backdrop-blur-sm px-4 md:px-8 py-3">
            <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">Sul podio della giornata</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {mvp.runners_up.map((r, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => openBreakdown(r.user_id, r.team_name)}
                  disabled={!r.user_id}
                  className="flex items-center gap-2 text-sm text-left w-full hover:bg-white/5 rounded-lg px-2 py-1 -mx-2 transition-colors disabled:cursor-default disabled:hover:bg-transparent"
                  data-testid={`mvp-runner-${i}`}
                >
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full font-black text-[10px] shrink-0"
                        style={{ background: i === 0 ? "#e5e7eb" : "#f0b374", color: "#0f172a" }}>
                    {i + 2}
                  </span>
                  {r.girone && <GironeBadge girone={r.girone} size={16} />}
                  <div className="min-w-0 flex-1">
                    <div className="display font-black text-white uppercase truncate leading-tight text-base md:text-lg tracking-tight">
                      {r.team_name || r.coach_name}
                    </div>
                    {r.team_name && r.coach_name && (
                      <div className="text-[11px] text-white/65 truncate leading-tight">{r.coach_name}</div>
                    )}
                  </div>
                  <span className="ml-auto tabular-nums font-black" style={{ color: "#facc15" }}>{r.points?.toFixed(1)}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <MvpBreakdownModal
        matchday={breakdown?.matchday}
        teamId={breakdown?.teamId}
        teamName={breakdown?.teamName}
        onClose={() => setBreakdown(null)}
      />
    </section>
  );
}
